import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import React from "react";
import type { ToolParameterObject } from "../shared/mcp-chat-types";
import { formatAgentDisplayName } from "./mcp-chat-specialist-report-parser";
import { getToolDescription, isReadOnlyTool } from "./mcp-tool-descriptions";

export interface ToolActivity {
  requestId: string;
  toolCallId?: string;
  toolName: string;
  input: ToolParameterObject;
  status: "running" | "completed" | "error" | "blocked";
  isMutating: boolean;
  description: string;
  result?: unknown;
  error?: string;
  startTime?: number;
  duration?: number;
  /** Specialist-agent id when the activity is a synthesized specialist run (e.g. "wirer", "binder"). */
  specialistAgent?: string;
  /** Final accumulated specialist text (parseable JSON) once status is completed/error. */
  specialistAccumulated?: string;
}

/** Tool name prefix used for synthesized specialist activities. Must stay in sync with mcp-chat-tool-transcript.ts. */
export const SPECIALIST_TOOL_PREFIX = "specialist:";

interface McpToolActivityListProps {
  activities: ToolActivity[];
}

export const SpinnerIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="mcp-activity__spinner-icon">
    <circle
      cx="7"
      cy="7"
      r="5.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeDasharray="24 10"
    />
  </svg>
);

export const CheckIcon: React.FC = () => {
  const shouldReduceMotion = useReducedMotion();
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      {shouldReduceMotion ? (
        <path
          d="M3.5 7.5L5.5 9.5L10.5 4.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <motion.path
          d="M3.5 7.5L5.5 9.5L10.5 4.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.3, type: "spring", stiffness: 300 }}
        />
      )}
    </svg>
  );
};

export const ErrorIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M4.5 4.5L9.5 9.5M9.5 4.5L4.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const itemVariants = {
  initial: { opacity: 0, y: 8, height: 0 },
  animate: {
    opacity: 1,
    y: 0,
    height: "auto",
    transition: { type: "spring", stiffness: 400, damping: 30 }
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: { duration: 0.2 }
  }
};

const reducedMotionVariants = {
  initial: { opacity: 0, height: "auto" },
  animate: {
    opacity: 1,
    height: "auto",
    transition: { duration: 0.01 }
  },
  exit: {
    opacity: 0,
    height: "auto",
    transition: { duration: 0.01 }
  }
};

const McpToolActivityItem: React.FC<{ activity: ToolActivity }> = ({ activity }) => {
  const { status, description } = activity;
  const readOnly = isReadOnlyTool(activity.toolName);
  const shouldReduceMotion = useReducedMotion();

  let statusClass = "mcp-activity__item--write";
  if (status === "error") {
    statusClass = "mcp-activity__item--error";
  } else if (status === "completed") {
    statusClass = "mcp-activity__item--completed";
  } else if (readOnly) {
    statusClass = "mcp-activity__item--read";
  }

  return (
    <motion.div
      className={`mcp-activity__item ${statusClass}`}
      variants={shouldReduceMotion ? reducedMotionVariants : itemVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      layout={!shouldReduceMotion}
    >
      <span className="mcp-activity__icon">
        {status === "running" && <SpinnerIcon />}
        {status === "completed" && <CheckIcon />}
        {status === "error" && <ErrorIcon />}
      </span>
      <span className="mcp-activity__text">{description}</span>
      {status === "running" && <span className="mcp-activity__dot" />}
    </motion.div>
  );
};

export const McpToolActivityList: React.FC<McpToolActivityListProps> = ({ activities }) => (
  <div className="mcp-activity">
    <AnimatePresence>
      {activities.map((a) => (
        <McpToolActivityItem key={a.requestId} activity={a} />
      ))}
    </AnimatePresence>
  </div>
);

export function createToolActivity(detail: {
  requestId: string;
  toolCallId?: string;
  toolName: string;
  input: ToolParameterObject;
  status: "running" | "completed" | "error" | "blocked";
  isMutating: boolean;
  result?: unknown;
  error?: string;
  /** Optional — when this activity is a specialist run, carries the raw accumulated assistant text. */
  specialistAccumulated?: string;
}): ToolActivity {
  // Specialist activities are synthesized by dispatchStreamSpecialistActivity
  // with toolName = "specialist:<agent>". Strip the prefix onto specialistAgent
  // and derive a human-readable description ("Wirer run" / "Binder run" / etc.)
  // so the live indicator shows the right label before the transcript view
  // takes over with its custom specialist header.
  if (detail.toolName.startsWith(SPECIALIST_TOOL_PREFIX)) {
    const agent = detail.toolName.slice(SPECIALIST_TOOL_PREFIX.length);
    const displayName = formatAgentDisplayName(agent);
    return {
      ...detail,
      description: `${displayName} run`,
      specialistAgent: agent,
      specialistAccumulated: detail.specialistAccumulated
    };
  }
  return {
    ...detail,
    description: getToolDescription(detail.toolName, detail.input)
  };
}
